import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './ChatComponent.css';

// Icons for the chat
const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
  </svg>
);

const TeacherIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
);

const StudentIcon = () => (
  <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
  </svg>
);

const LoadingSpinner = () => (
  <div className="spinner">
    <div className="bounce1"></div>
    <div className="bounce2"></div>
    <div className="bounce3"></div>
  </div>
);

const ChatComponent = () => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'history'
  
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/messages`;
  const token = localStorage.getItem('authToken');

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch messages on component mount
  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      try {
        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setMessages(response.data.messages || []);
      } catch (err) {
        setError('حدث خطأ في تحميل المحادثة. يرجى المحاولة مرة أخرى.');
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [apiUrl, token]);

  // Handle sending message
  const handleSendMessage = async () => {
    if (newMessage.trim() === '') return;

    setSending(true);
    setError('');

    try {
      await axios.post(
        apiUrl,
        { message: newMessage },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Clear input and show success message
      setNewMessage('');
      setSuccessMessage('📬 تم إرسال رسالتك بنجاح وسوف يتم الرد من قبل المدرس');
      
      // Refresh messages
      const response = await axios.get(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setMessages(response.data.messages || []);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
      
      // Switch to history tab to show the new message
      setActiveTab('history');
      
    } catch (err) {
      setError('❌ فشل في إرسال الرسالة. يرجى المحاولة مرة أخرى.');
    } finally {
      setSending(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format message time
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get today's messages
  const getTodayMessages = () => {
    const today = new Date().toDateString();
    return messages.filter(msg => {
      const msgDate = new Date(msg.created_at).toDateString();
      return msgDate === today;
    });
  };

  // Get older messages
  const getOlderMessages = () => {
    const today = new Date().toDateString();
    return messages.filter(msg => {
      const msgDate = new Date(msg.created_at).toDateString();
      return msgDate !== today;
    });
  };

  const todayMessages = getTodayMessages();
  const olderMessages = getOlderMessages();

  return (
    <div className="chat-component" ref={chatContainerRef}>
      {/* Chat Header */}
      <div className="chat-header">
        <div className="header-content">
          <div className="teacher-avatar">
            <TeacherIcon />
          </div>
          <div className="header-info">
            <h3>التواصل مع المعلم</h3>
            <p className="teacher-status">
              <span className="status-dot online"></span>
              متصل الآن
            </p>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="chat-tabs">
          <button 
            className={`tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
          >
            ✏️ محادثة جديدة
          </button>
          <button 
            className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            📜 سجل المحادثات
            {messages.length > 0 && (
              <span className="message-count">{messages.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* Chat Body */}
      <div className="chat-body">
        {activeTab === 'chat' ? (
          /* New Chat Tab */
          <div className="new-chat-tab">
            <div className="welcome-message">
              <div className="welcome-icon">💬</div>
              <h4>مرحباً بك في الدردشة</h4>
              <p>يمكنك طرح أسئلتك هنا وسيقوم المدرس بالرد عليك في أقرب وقت</p>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="success-notification">
                <span className="success-icon">✅</span>
                <p>{successMessage}</p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="error-notification">
                <span className="error-icon">❌</span>
                <p>{error}</p>
              </div>
            )}

            {/* Message Input */}
            <div className="message-input-container">
              <div className="input-wrapper">
                <textarea
                  className="message-textarea"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="اكتب سؤالك هنا..."
                  rows="3"
                  disabled={sending}
                />
                <div className="input-actions">
                  <button 
                    className="send-button"
                    onClick={handleSendMessage}
                    disabled={sending || newMessage.trim() === ''}
                  >
                    {sending ? (
                      <LoadingSpinner />
                    ) : (
                      <>
                        <SendIcon />
                        <span>إرسال</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
              <p className="input-hint">
                اضغط <kbd>Enter</kbd> للإرسال • <kbd>Shift + Enter</kbd> للسطر الجديد
              </p>
            </div>
          </div>
        ) : (
          /* History Tab */
          <div className="history-tab">
            {loading ? (
              <div className="loading-messages">
                <LoadingSpinner />
                <p>جاري تحميل المحادثة...</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="empty-history">
                <div className="empty-icon">📭</div>
                <h4>لا توجد محادثات سابقة</h4>
                <p>ابدأ محادثة جديدة مع المدرس</p>
              </div>
            ) : (
              <div className="messages-container">
                {/* Today's Messages */}
                {todayMessages.length > 0 && (
                  <div className="messages-section">
                    <div className="section-header">
                      <span className="section-title">اليوم</span>
                      <span className="section-badge">{todayMessages.length} رسالة</span>
                    </div>
                    <div className="messages-list">
                      {todayMessages.map((msg, index) => (
                        <div 
                          key={index} 
                          className={`message-bubble ${msg.sender === 'teacher' ? 'teacher' : 'student'}`}
                        >
                          <div className="message-header">
                            <div className="sender-info">
                              <div className="sender-icon">
                                {msg.sender === 'teacher' ? <TeacherIcon /> : <StudentIcon />}
                              </div>
                              <span className="sender-name">
                                {msg.sender === 'teacher' ? 'المعلم' : 'أنت'}
                              </span>
                            </div>
                            <span className="message-time">{formatTime(msg.created_at)}</span>
                          </div>
                          <div className="message-content">
                            <p>{msg.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Older Messages */}
                {olderMessages.length > 0 && (
                  <div className="messages-section">
                    <div className="section-header">
                      <span className="section-title">محادثات سابقة</span>
                      <span className="section-badge">{olderMessages.length} رسالة</span>
                    </div>
                    <div className="messages-list">
                      {olderMessages.map((msg, index) => (
                        <div 
                          key={index} 
                          className={`message-bubble ${msg.sender === 'teacher' ? 'teacher' : 'student'}`}
                        >
                          <div className="message-header">
                            <div className="sender-info">
                              <div className="sender-icon">
                                {msg.sender === 'teacher' ? <TeacherIcon /> : <StudentIcon />}
                              </div>
                              <span className="sender-name">
                                {msg.sender === 'teacher' ? 'المعلم' : 'أنت'}
                              </span>
                            </div>
                            <span className="message-time">{formatTime(msg.created_at)}</span>
                          </div>
                          <div className="message-content">
                            <p>{msg.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Scroll anchor */}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Chat Footer */}
      <div className="chat-footer">
        <div className="footer-info">
          <p className="response-time">
            ⏱️ متوسط وقت الرد: خلال 24 ساعة
          </p>
          <p className="working-hours">
            🕒 أوقات الدردشة: من الساعة 9 صباحاً حتى 9 مساءً
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatComponent;